import styled from "styled-components";
import { breakpoints } from "styles/constants/breakpoints";
import { sizes } from "styles/constants/sizes";

export const BackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 16px 0px;
  background-color: transparent;
  gap: 10px;
  width: 100%;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.3;
  cursor: pointer;
`;

export const Card = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  padding: 24px 16px;
  position: relative;
  @media ${breakpoints.tablet} {
    flex-grow: unset;
    padding: 32px;
    width: ${sizes.checkoutCardWidth.tablet};
    box-shadow: 0 0 30px ${({ theme }) => theme.colors.transparent.white10};
  }
`;

export const CardHeading = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSizes["2hxl"]};
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.neutral[900]};
  margin: 0;
`;

export const CardSubtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 400;
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.neutral[600]};
  margin: 0 0 16px 0;
`;

export const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

export const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
`;

export const ProgressBarBackground = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.blue[50]};
  border-radius: 40px;
`;

export const ProgressBarFill = styled.div<{ progress: number }>`
  width: ${({ progress }) => progress}%;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.brand[500]};
  border-radius: 40px;
  position: absolute;
  top: 0;
  left: 0;
`;

export const ProgressText = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.brand[500]};
  margin: 0;
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.neutral[1000]};
`;

export const InputContainer = styled.div<{ disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
`;

// Phone input specific styles
export const PhoneInputContainer = styled.div`
  display: flex;
  gap: 8px;
`;

export const CountryCodeContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background-color: ${({ theme }) => theme.colors.blue[50]};
  border: 2px solid ${({ theme }) => theme.colors.periwinkle[500]};
  border-radius: 6px;
  gap: 10px;
  width: 84px;
`;

export const FlagIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CountryCodeText = styled.span`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 400;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.neutral[1000]};
`;

export const StyledInput = styled.input<{
  disabled?: boolean;
  hasIcon?: boolean;
  hasError?: boolean;
}>`
  width: 100%;
  height: 40px;
  padding: 0px 12px;
  ${({ hasIcon }) => hasIcon && "padding-right: 40px;"}
  border: 2px solid
    ${({ theme, hasError }) =>
    hasError ? theme.colors.magenta[400] : theme.colors.periwinkle[500]};
  border-radius: 6px;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 400;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.neutral[1000]};
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.blue[50] : theme.colors.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.brand[500]};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.neutral[1000]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral[600]};
  }
`;

export const SuggestionList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.white};
  max-height: 150px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

export const SuggestionItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.black[100]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral[100]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const PhoneInput = styled(StyledInput)`
  flex: 1;
`;

export const IconContainer = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

export const HintText = styled.p<{ isError?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-weight: 400;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  line-height: 1.33;
  color: ${({ isError, theme }) =>
    isError ? theme.colors.magenta[400] : theme.colors.neutral[700]};
  margin: 0;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const SubmitButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 13px 24px;
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.neutral[400] : theme.colors.brand[500]};
  border: none;
  border-radius: 6px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.375;
  color: ${({ theme }) => theme.colors.white};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  min-width: 172px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.brand[600]};
  }
`;
